import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styled from 'styled-components';
import API_URL from '../config';

// ─── Styled ───────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04);
  margin-bottom: 1.5rem;
`;

const FormatGuide = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #4f46e5;
  border-radius: 8px;
  padding: 1rem 1.1rem;
  margin-bottom: 1.5rem;
`;

const GuideTitle = styled.p`
  margin: 0 0 0.5rem;
  font-size: 12px;
  font-weight: 700;
  color: #4f46e5;
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const GuideRow = styled.p`
  margin: 0.2rem 0;
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 12.5px;
  color: #334155;
`;

const GuideNote = styled.p`
  margin: 0.625rem 0 0;
  font-size: 12px;
  color: #94a3b8;
`;

const DropArea = styled.div`
  border: 2px dashed ${p => p.isDragActive ? '#4f46e5' : '#cbd5e1'};
  border-radius: 12px;
  background: ${p => p.isDragActive ? '#eef2ff' : '#f8fafc'};
  padding: 2.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 1rem;

  &:hover {
    border-color: #4f46e5;
    background: #eef2ff;
  }
`;

const DropIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #e0e7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.125rem;
  svg { color: #4f46e5; }
`;

const DropText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #475569;
  text-align: center;
  strong { color: #4f46e5; }
`;

const DropHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 1.25rem;
`;

const statusColor = {
  pending:   '#4f46e5',
  uploading: '#f59e0b',
  success:   '#10b981',
  error:     '#ef4444',
};

const FileRow = styled.div`
  padding: 0.75rem 0.875rem;
  border-radius: 9px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 3px solid ${p => {
    if (p.status === 'success') return '#10b981';
    if (p.status === 'error')   return '#ef4444';
    if (p.status === 'uploading') return '#f59e0b';
    return p.isValid ? '#4f46e5' : '#ef4444';
  }};
  transition: border-color 0.15s;
`;

const FileRowTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

const FileName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  word-break: break-all;
  flex: 1;
`;

const FileSize = styled.span`
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
`;

const FileBadge = styled.span`
  padding: 0.15rem 0.5rem;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: ${p => {
    if (p.status === 'success')  return '#d1fae5';
    if (p.status === 'error')    return '#fee2e2';
    if (p.status === 'uploading') return '#fef3c7';
    return '#e0e7ff';
  }};
  color: ${p => {
    if (p.status === 'success')  return '#059669';
    if (p.status === 'error')    return '#dc2626';
    if (p.status === 'uploading') return '#d97706';
    return '#4f46e5';
  }};
`;

const SmallBtn = styled.button`
  padding: 0.25rem 0.625rem;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;
  background: ${p => p.danger ? '#fee2e2' : '#e0e7ff'};
  color: ${p => p.danger ? '#dc2626' : '#4f46e5'};
  &:hover { opacity: 0.75; }
`;

const ParsedInfo = styled.p`
  margin: 0.375rem 0 0;
  font-size: 12px;
  color: ${p => p.isValid ? '#059669' : '#dc2626'};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StatusInfo = styled.p`
  margin: 0.375rem 0 0;
  font-size: 12px;
  color: ${p => statusColor[p.status] || '#64748b'};
`;

const ErrorMsg = styled.p`
  font-size: 13px;
  color: #ef4444;
  margin: 0.5rem 0 0;
`;

const SubmitBtn = styled.button`
  width: 100%;
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 9px;
  background: ${p => p.disabled ? '#e2e8f0' : '#4f46e5'};
  color: ${p => p.disabled ? '#94a3b8' : '#fff'};
  font-size: 14.5px;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.15s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #4338ca;
    transform: translateY(-1px);
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseFilename(filename) {
  const match = filename.match(/^(AM|AV|TV)_(\d{4})-(\d{1,2})_(.+)\.pdf$/i);
  if (!match) return null;
  return {
    section: match[1].toUpperCase(),
    year: match[2],
    number: match[3],
    displayName: match[4],
  };
}

function makeId(file) { return `${file.name}-${file.size}`; }

const statusLabel = {
  pending:   'Ожидает',
  uploading: 'Загрузка…',
  success:   'Загружен',
  error:     'Ошибка',
};

// ─── Component ────────────────────────────────────────────────────────────────

function UploadMagazine({ theme, savedPassword }) {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');

  const isUploading = files.some(f => f.status === 'uploading');

  const updateFileStatus = useCallback((id, patch) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(prev => {
      const existingIds = new Set(prev.map(f => f.id));
      const newEntries = acceptedFiles
        .map(file => ({ id: makeId(file), file, parsed: parseFilename(file.name), status: 'pending', errorMessage: null }))
        .filter(e => !existingIds.has(e.id));
      return [...prev, ...newEntries];
    });
    setError('');
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
  });

  const handleRemove = (id) => setFiles(prev => prev.filter(f => f.id !== id));

  const uploadSingleFile = useCallback(async (entry) => {
    updateFileStatus(entry.id, { status: 'uploading', errorMessage: null });
    const formData = new FormData();
    formData.append('pdf', entry.file);
    formData.append('password', savedPassword);
    try {
      const response = await fetch(`${API_URL}/api/magazine/upload`, { method: 'POST', body: formData });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        updateFileStatus(entry.id, { status: 'error', errorMessage: body.message || 'Ошибка загрузки' });
        return 'error';
      }
      updateFileStatus(entry.id, { status: 'success', errorMessage: null });
      return 'success';
    } catch {
      updateFileStatus(entry.id, { status: 'error', errorMessage: 'Нет соединения с сервером' });
      return 'error';
    }
  }, [savedPassword, updateFileStatus]);

  const handleRetry = async (entry) => {
    updateFileStatus(entry.id, { status: 'pending', errorMessage: null });
    await uploadSingleFile({ ...entry, status: 'pending' });
  };

  const handleSubmit = async () => {
    setError('');
    if (files.length === 0) { setError('Добавьте хотя бы один PDF файл'); return; }
    if (files.some(f => !f.parsed)) { setError('Исправьте имена файлов с неверным форматом или удалите их'); return; }

    const pending = files.filter(f => f.status === 'pending' || f.status === 'error');
    const results = await Promise.allSettled(pending.map(uploadSingleFile));
    const successCount = results.filter(r => r.status === 'fulfilled' && r.value === 'success').length;

    if (successCount === pending.length) {
      toast.success('Все журналы успешно загружены!');
      setFiles([]);
    } else {
      toast.warn(`Загружено ${successCount} из ${pending.length}. Проверьте ошибки и повторите.`);
      setFiles(prev => prev.filter(f => f.status === 'error').map(f => ({ ...f, status: 'pending' })));
    }
  };

  const pendingCount = files.filter(f => f.status === 'pending' || f.status === 'error').length;
  const allValid    = files.length > 0 && files.every(f => f.parsed !== null);
  const canSubmit   = allValid && !isUploading && pendingCount > 0;

  const submitLabel = isUploading
    ? 'Загрузка…'
    : pendingCount > 0
      ? `Загрузить ${pendingCount} ${pendingCount === 1 ? 'файл' : pendingCount < 5 ? 'файла' : 'файлов'}`
      : 'Загрузить';

  return (
    <Card>
      <FormatGuide>
        <GuideTitle>Формат имени файла</GuideTitle>
        <GuideRow>AM_2026-04_Усыновлённые любовью.pdf</GuideRow>
        <GuideRow>AV_2026-2_Весть о здоровье.pdf</GuideRow>
        <GuideRow>TV_2025-12_Декабрь.pdf</GuideRow>
        <GuideNote>Формат: [РАЗДЕЛ]_[YYYY-MM]_[Название].pdf · Данные считываются из имени автоматически</GuideNote>
      </FormatGuide>

      <DropArea {...getRootProps()} isDragActive={isDragActive}>
        <input {...getInputProps()} />
        <DropIcon>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="12" y1="18" x2="12" y2="12"/>
            <line x1="9" y1="15" x2="15" y2="15"/>
          </svg>
        </DropIcon>
        <DropText>
          {isDragActive ? 'Отпустите файлы…' : <><strong>Выберите PDF файлы</strong> или перетащите сюда</>}
        </DropText>
        <DropHint>Только .pdf · Несколько файлов за раз</DropHint>
      </DropArea>

      {files.length > 0 && (
        <FileList>
          {files.map(entry => (
            <FileRow key={entry.id} isValid={!!entry.parsed} status={entry.status}>
              <FileRowTop>
                <FileName>{entry.file.name}</FileName>
                <FileSize>{(entry.file.size / 1024 / 1024).toFixed(2)} MB</FileSize>
                <FileBadge status={entry.status === 'pending' && !entry.parsed ? 'error' : entry.status}>
                  {entry.status === 'pending' && !entry.parsed ? 'Неверный формат' : statusLabel[entry.status]}
                </FileBadge>
                {entry.status === 'pending' && (
                  <SmallBtn danger onClick={() => handleRemove(entry.id)}>Удалить</SmallBtn>
                )}
                {entry.status === 'error' && (
                  <SmallBtn onClick={() => handleRetry(entry)}>Повторить</SmallBtn>
                )}
              </FileRowTop>

              {entry.status === 'pending' && entry.parsed && (
                <ParsedInfo isValid>
                  ✓ {entry.parsed.section} · {entry.parsed.year} · №{entry.parsed.number} · {entry.parsed.displayName}
                </ParsedInfo>
              )}
              {entry.status === 'pending' && !entry.parsed && (
                <ParsedInfo>
                  ✗ Ожидается: AM_2026-04_Название.pdf
                </ParsedInfo>
              )}
              {entry.status === 'uploading' && <StatusInfo status="uploading">Загружается на сервер…</StatusInfo>}
              {entry.status === 'success'   && <StatusInfo status="success">Файл успешно загружен</StatusInfo>}
              {entry.status === 'error'     && <StatusInfo status="error">{entry.errorMessage}</StatusInfo>}
            </FileRow>
          ))}
        </FileList>
      )}

      {error && <ErrorMsg>⚠ {error}</ErrorMsg>}

      <SubmitBtn onClick={handleSubmit} disabled={!canSubmit}>
        {isUploading ? (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1s linear infinite'}}>
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
            Загрузка…
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            {submitLabel}
          </>
        )}
      </SubmitBtn>

      <ToastContainer position="bottom-right" />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Card>
  );
}

export default UploadMagazine;
